import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { 
  createCollection, 
  updateCollection, 
  getCollectionById
} from '../services/collectionService';
import { ProductService } from '../services/productService';
import { Collection, CreateCollectionRequest, UpdateCollectionRequest } from '../types/collection';
import { Product } from '../types/product';
import useToastStore from '../store/useToastStore';
import { 
  ArrowLeft, 
  Save, 
  Layers,
  Search,
  Plus,
  X,
  GripVertical
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';

const AddOrEditCollectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { collectionId } = useParams<{ collectionId: string }>();
  const isEditing = Boolean(collectionId);
  
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [collection, setCollection] = useState<Collection | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  
  const { showToast } = useToastStore();

  // Load collection data if editing
  useEffect(() => {
    if (isEditing && collectionId) {
      loadCollection(collectionId);
    }
  }, [isEditing, collectionId]);

  const loadCollection = async (id: string) => {
    try {
      setIsLoading(true);
      const data = await getCollectionById(id);
      setCollection(data);
      setFormData({
        name: data.name,
        description: data.description
      });
      
      // Load selected products
      if (data.products && data.products.length > 0) {
        setSelectedProducts(data.products);
      }
    } catch (error) {
      console.error('Error loading collection:', error);
      showToast('Failed to load collection', 'error');
      navigate('/collections');
    } finally {
      setIsLoading(false);
    }
  };

  // This function is no longer needed since we get products directly from the collection
  // const loadSelectedProducts = async (productIds: string[]) => { ... }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const searchProducts = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await ProductService.searchProducts(query);
      // Filter out already selected products
      const filteredResults = results.filter(
        product => !selectedProducts.find(selected => selected._id === product._id)
      );
      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching products:', error);
      showToast('Failed to search products', 'error');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      searchProducts(query);
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const addProduct = (product: Product) => {
    if (!selectedProducts.find(p => p._id === product._id)) {
      setSelectedProducts(prev => [...prev, product]);
      setSearchResults(prev => prev.filter(p => p._id !== product._id));
    }
  };

  const removeProduct = (productId: string) => {
    setSelectedProducts(prev => prev.filter(p => p._id !== productId));
  };

  const handleReorderProducts = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(selectedProducts);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSelectedProducts(items);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showToast('Collection name is required', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      let collectionResult: Collection;
      
      if (isEditing && collectionId) {
        // Update existing collection
        const updates: UpdateCollectionRequest = {
          name: formData.name.trim(),
          description: formData.description.trim()
        };
        
        // Include products in the update
        const updatesWithProducts: UpdateCollectionRequest = {
          ...updates,
          products: selectedProducts
        };
        
        collectionResult = await updateCollection(collectionId, updatesWithProducts);
      } else {
        // Create new collection
        const newCollection: CreateCollectionRequest = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          products: selectedProducts
        };
        
        collectionResult = await createCollection(newCollection);
      }
      
      showToast(`Collection ${isEditing ? 'updated' : 'created'} successfully!`, 'success');
      navigate('/collections');
    } catch (error) {
      console.error('Error saving collection:', error);
      showToast(`Failed to ${isEditing ? 'update' : 'create'} collection`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/collections');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="space-y-6">
                <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
                <div className="h-10 bg-gray-200 rounded w-1/4"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Layers className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {isEditing ? 'Edit Collection' : 'Create New Collection'}
                </h1>
                <p className="text-gray-600">
                  {isEditing 
                    ? 'Update your collection details and settings'
                    : 'Create a new collection to organize your products'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Collection Details</h2>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Collection Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Best Sellers, New Arrivals, Today's Offers"
                    required
                    disabled={isSubmitting}
                    className="h-10"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <Input
                    id="description"
                    name="description"
                    type="text"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description in one line"
                    disabled={isSubmitting}
                    className="h-10"
                  />
                </div>
              </div>
            </div>

            {/* Product Selection */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Add Products</h2>
              
              {/* Search Box */}
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search products to add to this collection..."
                    className="pl-10 h-10"
                    disabled={isSubmitting}
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Search Results</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {searchResults.map((product) => (
                      <div
                        key={product._id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          {product.images[0] && (
                            <img
                              src={product.images[0]}
                              alt={product.title}
                              className="w-10 h-10 object-cover rounded"
                            />
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">{product.title}</p>
                            <p className="text-xs text-gray-500">₹{product.discountedPrice || product.mrp}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => addProduct(product)}
                          className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100"
                        >
                          <Plus className="w-3 h-3" />
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Products */}
              {selectedProducts.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-700">
                      Selected Products ({selectedProducts.length})
                    </h3>
                    <span className="text-xs text-gray-500">Drag to reorder</span>
                  </div>
                  <DragDropContext onDragEnd={handleReorderProducts}>
                    <Droppable droppableId="selected-products">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="space-y-2 max-h-60 overflow-y-auto"
                        >
                          {selectedProducts.map((product, index) => (
                            <Draggable key={product._id} draggableId={product._id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`flex items-center justify-between p-3 border border-green-200 rounded-lg bg-green-50 transition-colors ${
                                    snapshot.isDragging ? 'bg-green-100 shadow-md' : 'hover:bg-green-100'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                      <div
                                        {...provided.dragHandleProps}
                                        className="cursor-grab hover:cursor-grabbing p-1 rounded hover:bg-green-200"
                                      >
                                        <GripVertical className="w-4 h-4 text-gray-400" />
                                      </div>
                                      <span className="text-xs font-medium text-gray-500 bg-white px-2 py-1 rounded">
                                        {index + 1}
                                      </span>
                                    </div>
                                    {product.images[0] && (
                                      <img
                                        src={product.images[0]}
                                        alt={product.title}
                                        className="w-10 h-10 object-cover rounded"
                                      />
                                    )}
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{product.title}</p>
                                      <p className="text-xs text-gray-500">₹{product.discountedPrice || product.mrp}</p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeProduct(product._id)}
                                    className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100"
                                  >
                                    <X className="w-3 h-3" />
                                    Remove
                                  </button>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              )}

              {selectedProducts.length === 0 && !searchQuery && (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">Search for products to add to your collection</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formData.name.trim()}
                className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {isEditing ? 'Update Collection' : 'Create Collection'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOrEditCollectionPage; 